import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, useLocation, useHistory, Redirect } from 'react-router-dom';
import { checkIsModal, ROUTES, usePreviousNormalLocation } from '~/Routes';
import Market from './Market';
import PrivacyDialog from './PrivacyDialog';
import Header from '../components/Header';
import Footer from './Footer';
import { toast } from 'react-toastify';
import { useStoreState, useStoreActions } from '~/store';
import { useIntl } from 'react-intl';
import ScrollTopButton from 'Components/ScrollTopButton';
import { useNotifications } from '~/notifications';
import useTrusted from '~/trusted';
import TopNav from 'Components/TopNav';
import { RoutingTable } from '~/router';
import Tenders from './Tenders';
import Banner from './Banner';
import { useLazyRoutes } from '~/router/useLazyRoutes';
import Home from './Home';

const Modals = lazy(() => import('./Modals'));

const Body = ({ home }: { home: boolean }) => {
  const lazyRoutes = useLazyRoutes();
  const solution = useStoreState((state) => state.auth.solution);
  const user = useStoreState((state) => state.auth.user);
  const { formatMessage } = useIntl();

  const setParticipantToast = useStoreActions(
    (state) => state.auth.setParticipantToast
  );

  const location = useLocation();
  const history = useHistory();

  const prevLocation = usePreviousNormalLocation(location) || {
    pathname: ROUTES.home,
  };

  const isModal = checkIsModal(location.pathname);

  useEffect(() => {
    if ((solution?.new_participants ?? 0) > 0) {
      const toastId = toast(
        formatMessage(
          {
            id: 'new-participants-toast',
            defaultMessage:
              'There {count, plural, one {is # new participant} other {are # new participants}} on the platform. Click here to review them.',
          },
          {
            count: solution.new_participants,
          }
        ),
        {
          autoClose: false,
          onClick: () => {
            history.push('/participants?dismissed=0');
          },
        }
      );
      setParticipantToast(toastId);
    }
  }, [solution]);

  const routes = [Tenders];

  return (
    <>
      <section id="content" className={home ? '' : 'main-content'} role="main">
        <main
          style={{
            '--optional-text': `"${formatMessage({
              id: 'form-optional',
              defaultMessage: 'Optional',
            })}"`,
          }}
        >
          <RoutingTable
            routes={routes}
            location={isModal ? prevLocation : location}
            lazyRoutes={lazyRoutes}
          >
            <Route path={ROUTES.homePage} exact>
              {user?.landingpage_access === false ? (
                <Redirect to="/" />
              ) : (
                <Home />
              )}
            </Route>
            <Route path={[ROUTES.home, ROUTES.marketplace]} exact>
              <Market />
            </Route>
          </RoutingTable>
        </main>
      </section>
      <Suspense fallback={null}>
        <Modals />
      </Suspense>
      <ScrollTopButton />
    </>
  );
};

export default function PageWrapper() {
  const solution = useStoreState((state) => state.auth.solution);
  const loading = useStoreState((state) => state.auth.loading);
  const privacyCheckReq = useStoreState((state) => state.auth.privacyCheckReq);
  const location = useLocation();
  const prevLocation = usePreviousNormalLocation(location);
  const [isHomeMode, setIsHomeMode] = useState(
    location.pathname === ROUTES.homePage
  );

  useEffect(() => {
    const isModal = checkIsModal(location.pathname);
    const effectivePathname = isModal
      ? prevLocation?.pathname
      : location.pathname;
    setIsHomeMode(effectivePathname === ROUTES.homePage);
  }, [location.pathname, prevLocation?.pathname]);

  useNotifications();
  useTrusted();

  if (!solution) return null;

  // Blank content area while switching solutions (keep topbar visible)
  if (loading) return <TopNav />;

  return (
    <>
      <TopNav />
      <Header home={isHomeMode} />
      {!isHomeMode && <Banner />}
      {privacyCheckReq ? <PrivacyDialog /> : <Body home={isHomeMode} />}
      <Footer home={isHomeMode} />
    </>
  );
}
